import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { isDesktopApp, isWebApp } from '../../../../submodules/engine-common/environment-util';
import { buildUrl } from '../../../../submodules/engine-common/util';
import { EnvironmentDataService } from './environment-data.service';
import { SecurityDataService } from './security-data.service';
import { RestErrorManager } from './errors/rest-error-manager';

@Injectable()
export class AuthenticationVerifierService  {
  
  isAuthenticated;
  userProfile;

  constructor(
    private _injector:Injector,
    private _router: Router,
    private _httpClient: HttpClient,
    private _environmentDataService: EnvironmentDataService,
    private _securityDataService: SecurityDataService) 
  { }

  public async check(routerStateSnapshot:RouterStateSnapshot):Promise<boolean> {

    this._debug('canActivate()', 'state.url', routerStateSnapshot.url, true)
    
    //Url originale di caricamento dell'applicazione
    const externalUrl = window.location.href;
    //Url di navigazione interna gestita da angular
    const internalUrl = routerStateSnapshot.url;

    this._debug('_check()', 'window.location.href (external url)', window.location.href)
    this._debug('_check()', 'state.url (internal url)', routerStateSnapshot.url)

    let pathFisrtElement = internalUrl.split("/")[1];
    
    // Anche in area pubblica è utile conoscere i dati del profilo (se l'utente è autenticato) per mostrare il nome dell'utente
    this.isAuthenticated = this._securityDataService.isAuthenticated();
    if(!this.isAuthenticated)
      this._debug('_checkWebApp()', 'STATO', '[Non autenticato]')
    else{
      const userProfile = this._securityDataService.userProfile
      if(!userProfile && isWebApp()) {
        this._debug('_checkWebApp()', 'STATO', '[Autenticato] [Non caricate authorities]')
         //Se non ho ancora caricato il profile con le relative authorities, allora le carico
        await this._securityDataService.setPrincipal();
        this.userProfile = this._securityDataService.userProfile
      } else
        this._debug('_checkWebApp()', 'STATO', '[Autenticato]')
    }
    

    if(pathFisrtElement.startsWith('private')){

      //DesktopApp
      if(isDesktopApp()){
        return this._checkDesktopApp();
      }
      //WebApp
      else{
        try {
            if(this._environmentDataService.environment.getValue("federatedLogin"))
              return await this._checkFederatedWebApp(externalUrl);
            else
              return await this._checkWebApp(externalUrl);
        } catch (error) {
            this._injector.get(RestErrorManager).manageError(error);
            return false;
        }
      
      }
    }
   
    // lascia andare a buon fine la navigazione corrente
    return true;
  }

  private async _checkWebApp(externalUrl) {
    //Se non sono ancora autenticato (no accessToken nel local storage)
    if(!this.isAuthenticated) {
      //Memorizza la pagina di destinazione in modo da poterlo reindirizzare dopo la login
      const urlFragments = externalUrl.split("/").slice(3);
      if(urlFragments[0]=='private')
        localStorage.setItem('redirectUrl', urlFragments.join("/"));
      else 
        localStorage.removeItem('redirectUrl');

      this._router.navigate(['/login']);

      return false;
    }
    // Se invece sono già autenticato
    else {
      const memorizedUrl = localStorage.getItem('redirectUrl');
      localStorage.removeItem('redirectUrl');

      if(memorizedUrl){
        //Routing verso pagina senza parametro code che deve essere welcome page dell'area privata
        this._router.navigate(memorizedUrl.split('/').filter(v => v != ''));
        return false;
      }
      else  
        return true;
    }
  }   

  private async _checkFederatedWebApp(externalUrl) {
    let oauth2CodeIndex = externalUrl.indexOf('code');

    //Se non sono ancora autenticato (1° o 2° giro)
    if(!this._securityDataService.isAuthenticated()) {

      //Se non sono ancora autenticato e non è presente un oauth2Code nel url (1° giro)
      if(oauth2CodeIndex == -1) {
        this._debug('_checkFederatedWebApp()', 'STATO', '[Non autenticato] [Non è presente un oauth2Code (1° giro)]')

        localStorage.setItem('redirectUrl', externalUrl.split("/").slice(3).join("/"));
        this._securityDataService.login(null);
        
        // blocca la navigazione corrente
        return false;
      }
      //Se non sono ancora autenticato ed è presente un oauth2Code nel url (2° giro)
      else{
        this._debug('_checkFederatedWebApp()', 'STATO', '[Non autenticato] [É presente un oauth2Code (2° giro)]')
  
          try{
            const accessToken = await this._fetchAccessTokenByOauth2Code(externalUrl.substring(oauth2CodeIndex + 5));
            
            //Memorizziamo il token
            localStorage.setItem("access_token", accessToken.access_token);
            
            //Valorizziamo il principal
            await this._securityDataService.setPrincipal();

            this._securityDataService.navigateAfterLogin();
            
            // blocca la navigazione corrente
            return false;
          }catch(e){
            console.log("Error retrieving access token", e);

            // blocca la navigazione corrente
            return false;
          }
      }
    } 
    //Se sono già autenticato (3° giro)
    else{
      this._debug('_checkFederatedWebApp()', 'STATO', '[Autenticato]');
      return true; 
    }
  }   
  
  private _checkDesktopApp = () => {
    if(!this._securityDataService.isAuthenticated()) {
      this._router.navigate(['/']);
      // blocca la navigazione corrente
      return false;
    }else
      return true;
  }  

  private async _fetchAccessTokenByOauth2Code(oauth2Code):Promise<any> {
    let params = new URLSearchParams();   
    params.append('grant_type','authorization_code');
    params.append('client_id', this._environmentDataService.environment.getValue("oauth2.clientId"));
    params.append('redirect_uri', buildUrl(this._environmentDataService.environment.getValue("oauth2.redirectEndpoint"),
                                           ["private"],
                                           null));
    params.append('code',oauth2Code);

    let headers = new HttpHeaders({'Content-type': 'application/x-www-form-urlencoded; charset=utf-8', 'Authorization': 'Basic '+btoa(this._environmentDataService.environment.getValue("oauth2.clientId")+":secret")});
    
    return await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.ssoApi"),
                                          ["oauth","token"],
                                          null),
                                 params.toString(), 
                                 { headers: headers })
                                 .toPromise();
  }

  private _debug(func, key, value, primary?: boolean) {
    let color, background;
    if(primary){
      color = 'white';
      background = 'red';
    }else{
      color = 'black';
      background = 'transparent';
    }
    console.log(`%c AuthenticationVerifierService.${func} - ${key}: ${value}`, `background: ${background}; color: ${color}; border:2px solid red; padding: 2px;`);
  }
}
